/**
 * useOnboardingGate - applies the setup-guide rule (lib/onboardingGate.ts) in the app shell: a new
 * user who lands on the dashboard is sent to /welcome, and everywhere else in the app they get a
 * slim banner. Called once, by AppLayout in App.tsx.
 *
 * Neither happens until a peek (lib/onboardingAvailability.ts) has confirmed the guide is switched
 * on for this user. The peek only runs when the rule would act, and is cached for the session.
 * The redirect also needs the answer promptly (REDIRECT_WINDOW_MS): a user the peek was slow for is
 * already using the dashboard, so they get the banner instead of being moved off it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getCachedAvailability, peekOnboardingAvailability } from '../lib/onboardingAvailability';
import { WELCOME_PATH, decideOnboardingGate, isFromGuide } from '../lib/onboardingGate';
import type { OnboardingGateDecision } from '../lib/onboardingGate';
import { isOnboardingSnoozed, snoozeOnboardingForSession } from '../lib/snooze';

/** How long after the rule first wants to redirect an availability answer may still cause one. */
const REDIRECT_WINDOW_MS = 1500;

export interface OnboardingGate {
  showBanner: boolean;
  /** Hides the banner and stops the dashboard redirect for the rest of this browser session. */
  dismissBanner(): void;
}

/**
 * Whether the guide is available to this user: true only after a successful peek that said so.
 * Asks (once per session) only while `needed`, so users the rule leaves alone cost no call.
 */
function useGuideAvailable(uid: string | null, needed: boolean): boolean {
  const [answer, setAnswer] = useState<{ uid: string; enabled: boolean } | null>(null);
  const known = !uid ? null : answer?.uid === uid ? answer.enabled : getCachedAvailability(uid);

  useEffect(() => {
    if (!uid || !needed || known !== null) return undefined;
    let active = true;
    void peekOnboardingAvailability(uid).then((enabled) => {
      if (active) setAnswer({ uid, enabled });
    });
    return () => {
      active = false;
    };
  }, [uid, needed, known]);

  return known === true;
}

export function useOnboardingGate(): OnboardingGate {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, loading, refreshProfile } = useAuth();

  // The snooze lives in sessionStorage; this only makes dismissing the banner re-render.
  const [, setSnoozeVersion] = useState(0);

  // Finishing or skipping setup is recorded on the server while the user is on /welcome, so the
  // profile read at sign-in is stale once they leave. Re-read it and hold the gate until then,
  // otherwise "Go to dashboard" would bounce straight back to /welcome.
  const [previousPath, setPreviousPath] = useState(pathname);
  const [rereadingProfile, setRereadingProfile] = useState(false);
  if (pathname !== previousPath) {
    setPreviousPath(pathname);
    if (previousPath === WELCOME_PATH) setRereadingProfile(true);
  }

  // The context hands out a new function on every render; the re-read below runs once per exit.
  const refreshProfileRef = useRef(refreshProfile);
  useEffect(() => {
    refreshProfileRef.current = refreshProfile;
  });
  useEffect(() => {
    if (!rereadingProfile) return undefined;
    let active = true;
    refreshProfileRef
      .current()
      .catch(() => undefined)
      .finally(() => {
        if (active) setRereadingProfile(false);
      });
    return () => {
      active = false;
    };
  }, [rereadingProfile]);

  // A tab opened from the guide is the user exploring on purpose: treat it as "finish later".
  const fromGuide = isFromGuide(search);
  useEffect(() => {
    if (fromGuide) snoozeOnboardingForSession();
  }, [fromGuide]);

  const wanted = decideOnboardingGate({
    loaded: !loading && user != null && userProfile != null,
    emailVerified: user?.emailVerified === true,
    accountStatus: userProfile?.status,
    preferences: userProfile?.preferences,
    pathname,
    snoozed: fromGuide || isOnboardingSnoozed(),
  });
  const guideAvailable = useGuideAvailable(user?.uid ?? null, wanted !== 'none');

  // The redirect window opens when the rule first wants to redirect and closes REDIRECT_WINDOW_MS
  // later if the availability answer has not arrived. An answer that was already cached arrives
  // with the window open. A window closed by a late answer stays closed until the user leaves
  // the dashboard, so the banner they were shown does not turn into a redirect later.
  const [redirectWindowOpen, setRedirectWindowOpen] = useState(true);
  const redirectPending = wanted === 'redirect' && !guideAvailable;
  useEffect(() => {
    if (wanted !== 'redirect') {
      setRedirectWindowOpen(true);
      return undefined;
    }
    if (!redirectPending) return undefined;
    const timer = window.setTimeout(() => setRedirectWindowOpen(false), REDIRECT_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [wanted, redirectPending]);

  const decision: OnboardingGateDecision = !guideAvailable
    ? 'none'
    : wanted === 'redirect' && !redirectWindowOpen
      ? 'banner'
      : wanted;

  // Passive, not a layout effect: on first mount a layout effect runs before the router has
  // subscribed to history, and the navigation would be lost.
  useEffect(() => {
    if (decision === 'redirect' && !rereadingProfile) navigate(WELCOME_PATH, { replace: true });
  }, [decision, rereadingProfile, navigate]);

  const dismissBanner = useCallback(() => {
    snoozeOnboardingForSession();
    setSnoozeVersion((version) => version + 1);
  }, []);

  return { showBanner: decision === 'banner' && !rereadingProfile, dismissBanner };
}
